/**
 * Only NRx and TRx are stored — Refills is derived (`deriveRefills`). The
 * source sends both, so TRx is authoritative rather than computed.
 */

import { TrendChartLineStyleType } from '../../shared/ind-trend-chart/ind-trend-chart.types';

export type RxMetricKey = 'TRx' | 'NRx' | 'Refills';
export type RxPeriodMode = 'weekly' | 'monthly';
export type RxTrendsView = 'graph' | 'table';

/** Weeks visible on first render; the rest are reached by swiping. */
export const RX_TRENDS_DEFAULT_WINDOW_SIZE = 6;

export interface RxStoredMetrics {
  NRx: (number | null)[];
  TRx: (number | null)[];
}

/** One product's series for one contact, as stored: numbers and anchors only. */
export interface RxStoredProductSeries {
  productId: string;
  productName: string;
  /** ISO date of the first weekly bucket. */
  weekAnchor: string;
  /** `YYYY-MM` of the first monthly bucket. */
  monthAnchor: string;
  weekly: RxStoredMetrics;
  monthly: RxStoredMetrics;
}

/** A stored series plus labels derived from its anchors at render time. */
export interface RxProductSeries extends RxStoredProductSeries {
  weeklyLabels: string[];
  monthlyLabels: string[];
  /** Competitor products are charted but never counted in the All total. */
  isCompetitor?: boolean;
}

/**
 * One column of the table view: the metrics when a product is selected, the
 * products plus Total when All is. `color`/`lineStyleType` mirror the series.
 */
export interface RxTrendTableColumn {
  label: string;
  color: string;
  lineStyleType: TrendChartLineStyleType;
}

export interface RxTrendTableRow {
  label: string;
  values: (number | null)[];
  /** Most recent bucket — carries the "Latest" badge. */
  isLatest: boolean;
}

/**
 * Decimal places carried by the source. Projected values arrive at three
 * places, so summing them unrounded yields float artefacts (`1.015 + 2.03`
 * → `3.0450000000000004`) that would render verbatim.
 */
export const RX_VALUE_DECIMAL_PLACES = 3;

/**
 * Whole prescriptions, half rounding up, per OMNI-60746.
 *
 * On QA1's ~1.0–2.3 projected values this collapses the line flat (OMNI-61589).
 * That is the rounding, not a broken series — raise it with product rather than
 * quietly adding places here.
 */
export const RX_DISPLAY_DECIMAL_PLACES = 0;

/**
 * Applied at the last step before render, to chart and table alike so a line,
 * its tooltip and its cell cannot disagree. Totals are summed at full precision
 * and rounded once, so the figure is the rounded true total rather than a sum
 * of rounded parts. Null stays null.
 */
export function roundRxValueForDisplay(value: number | null): number | null {
  if (value === null || value === undefined) return null;
  const scale = Math.pow(10, RX_DISPLAY_DECIMAL_PLACES);
  return Math.round(value * scale) / scale;
}

/**
 * Refills = TRx − NRx.
 *
 * Null when either input is absent: a difference cannot be recovered from one
 * operand, so treating a missing NRx as zero would report all of TRx as refills.
 * A negative result is also null — refills cannot be negative, so a TRx below
 * its own NRx is a source problem, not a measurement worth plotting.
 */
export function deriveRefills(totalRx: number | null, newRx: number | null): number | null {
  if (totalRx === null || totalRx === undefined) return null;
  if (newRx === null || newRx === undefined) return null;

  const scale = Math.pow(10, RX_VALUE_DECIMAL_PLACES);
  const refills = Math.round((totalRx - newRx) * scale) / scale;
  return refills < 0 ? null : refills;
}
